import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import SharePointMsalProvider from './components/SharePoint/SharePointMsalProvider';
import { SettingSaveProvider } from './contexts/SettingSaveContext';
import { FileTypeEnum } from './enums/FileTypeEnum';
import { SettingTypeEnum } from './enums/SettingTypeEnum';
import AuthLayout from './layouts/AuthLayout';
import ExternalLayout from './layouts/ExternalLayout';
import MainLayout from './layouts/MainLayout';
import type { OfficeRouteSegment } from './lib/officeRoutePath';
import { LEGACY_OFFICE_REDIRECTS } from './lib/officeRoutePath';
import ExtensionAuthorize from './routes/ExtensionAuthorize';
import Login from './routes/Login';
import Logout from './routes/Logout';
import MobileAuthSuccess from './routes/MobileAuthSuccess';
import OAuthCallback from './routes/OAuthCallback';
import Office from './routes/Office';
import ProtectedFile from './routes/ProtectedFile';
import ProtectedFileToken from './routes/ProtectedFileToken';
import QuickShare from './routes/QuickShare';
import TrashFiles from './routes/TrashFiles';
import Viewer from './routes/Viewer';
import { slugify } from './utils/utils';

/** Thin replace redirect for legacy /word|/excel|/powerpoint bookmarks. */
function LegacyOfficeRedirect({ to }: { to: OfficeRouteSegment }) {
  const { fileId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${to}/${fileId}${search}`} replace />;
}

const SharePointOffice = lazy(() => import('./routes/SharePointOffice'));
const Users = lazy(() => import('./routes/Users'));
const Groups = lazy(() => import('./routes/Groups'));
const SharedFiles = lazy(() => import('./routes/SharedFiles'));
const FavoritesFiles = lazy(() => import('./routes/FavoritesFiles'));
const RecentFiles = lazy(() => import('./routes/RecentFiles'));
const HiddenFiles = lazy(() => import('./routes/HiddenFiles'));
const ReceivedFiles = lazy(() => import('./routes/ReceivedFiles'));
const PersonalFiles = lazy(() => import('./routes/PersonalFiles'));
const Settings = lazy(() => import('./routes/Settings'));
const Store = lazy(() => import('./routes/Store'));
const ActivityLogs = lazy(() => import('./routes/ActivityLogs'));
const PaymentSuccess = lazy(() => import('./routes/PaymentSuccess'));
const Purchase = lazy(() => import('./routes/Purchase'));
const SalesDashboard = lazy(() => import('./routes/SalesDashboard'));
const Dashboard = lazy(() => import('./routes/Dashboard'));
const SettingCompanyBranding = lazy(() => import('./routes/settingPages/SettingCompanyBranding'));
const SettingAdminRegisteredMemberVisibility = lazy(
  () => import('./routes/settingPages/SettingAdminRegisteredMemberVisibility'),
);
const SettingDownloadRestrictions = lazy(
  () => import('./routes/settingPages/SettingDownloadRestrictions'),
);
const SettingDownloadAllowedTags = lazy(
  () => import('./routes/settingPages/SettingDownloadAllowedTags'),
);
const SettingForcedAntiCaptureMode = lazy(
  () => import('./routes/settingPages/SettingForcedAntiCaptureMode'),
);
const SettingFileSizeLimit = lazy(() => import('./routes/settingPages/SettingFileSizeLimit'));
const SettingCertifiedTimestamping = lazy(
  () => import('./routes/settingPages/SettingCertifiedTimestamping'),
);
const SettingContributorStorageLimit = lazy(
  () => import('./routes/settingPages/SettingContributorStorageLimit'),
);
const SettingDeviceAccessLimitation = lazy(
  () => import('./routes/settingPages/SettingDeviceAccessLimitation'),
);
const SettingEditorFileManagement = lazy(
  () => import('./routes/settingPages/SettingEditorFileManagement'),
);
const SettingExternalFileSharingRestriction = lazy(
  () => import('./routes/settingPages/SettingExternalFileSharingRestriction'),
);
const SettingFileSalesPermission = lazy(
  () => import('./routes/settingPages/SettingFileSalesPermission'),
);
const SettingFileTypeRestriction = lazy(
  () => import('./routes/settingPages/SettingFileTypeRestriction'),
);
const SettingFolderCreationPermission = lazy(
  () => import('./routes/settingPages/SettingFolderCreationPermission'),
);
const SettingGroupCreationControl = lazy(
  () => import('./routes/settingPages/SettingGroupCreationControl'),
);
const SettingOnBehalfMode = lazy(() => import('./routes/settingPages/SettingOnBehalfMode'));
const SettingOnlineEditors = lazy(() => import('./routes/settingPages/SettingOnlineEditors'));
const SettingPublicAccessLink = lazy(() => import('./routes/settingPages/SettingPublicAccessLink'));
const SettingRetentionPolicies = lazy(
  () => import('./routes/settingPages/SettingRetentionPolicies'),
);
const SettingSessionControl = lazy(() => import('./routes/settingPages/SettingSessionControl'));
const SettingSingleSignOn = lazy(() => import('./routes/settingPages/SettingSingleSignOn'));
const SettingFileTagManagement = lazy(
  () => import('./routes/settingPages/SettingFileTagManagement'),
);
const SettingFileTagAdditionRestriction = lazy(
  () => import('./routes/settingPages/SettingFileTagAdditionRestriction'),
);
const SettingUploadWithTag = lazy(() => import('./routes/settingPages/SettingUploadWithTag'));
const SettingWatermarkProtection = lazy(
  () => import('./routes/settingPages/SettingWatermarkProtection'),
);
const SettingAiTagging = lazy(() => import('./routes/settingPages/SettingAiTagging'));
const SettingFileDeletionRestriction = lazy(
  () => import('./routes/settingPages/SettingFileDeletionRestriction'),
);
const SettingAiFileSummary = lazy(() => import('./routes/settingPages/SettingAiFileSummary'));
const SettingAiFileChat = lazy(() => import('./routes/settingPages/SettingAiFileChat'));
const SettingAiScraping = lazy(() => import('./routes/settingPages/SettingAiScraping'));
const SettingResharingPermission = lazy(
  () => import('./routes/settingPages/SettingResharingPermission'),
);
const SettingFileVersioningRetentionPolicies = lazy(
  () => import('./routes/settingPages/SettingFileVersioningRetentionPolicies'),
);
const SettingMandatoryExpirationSharedFile = lazy(
  () => import('./routes/settingPages/SettingMandatoryExpirationSharedFile'),
);
const SettingSecureFileLocalAccess = lazy(
  () => import('./routes/settingPages/SettingSecureFileLocalAccess'),
);
const SettingTagBasedAccessControl = lazy(
  () => import('./routes/settingPages/SettingTagBasedAccessControl'),
);
const SettingFileSharingApprovals = lazy(
  () => import('./routes/settingPages/SettingFileSharingApproval'),
);
const SettingMandatoryFileClassification = lazy(
  () => import('./routes/settingPages/SettingMandatoryFileClassification'),
);
const SettingForcedFileInvisibility = lazy(
  () => import('./routes/settingPages/SettingForcedFileInvisibility'),
);
const SettingShortcutLink = lazy(() => import('./routes/settingPages/SettingShortcutLink'));
const SettingQuickShareMode = lazy(() => import('./routes/settingPages/SettingQuickShareMode'));
const SettingTotpRequired = lazy(() => import('./routes/settingPages/SettingTotpRequired'));
const SettingPasskeySettings = lazy(() => import('./routes/settingPages/SettingPasskeySettings'));
const SettingAntivirusScan = lazy(() => import('./routes/settingPages/SettingAntivirusScan'));
const AntivirusQuarantine = lazy(() => import('./routes/AntivirusQuarantine'));
const SettingAdminSettingsPermissions = lazy(
  () => import('./routes/settingPages/SettingAdminSettingsPermissions'),
);
const SettingIpRegionRestriction = lazy(
  () => import('./routes/settingPages/SettingIpRegionRestriction'),
);
const SettingSecurityAnalysis = lazy(() => import('./routes/settingPages/SettingSecurityAnalysis'));
const SettingActivityLogRetention = lazy(
  () => import('./routes/settingPages/SettingActivityLogRetention'),
);
const SettingSharePointIntegration = lazy(
  () => import('./routes/settingPages/SettingSharePointIntegration'),
);
const SettingActiveDirectoryLdap = lazy(
  () => import('./routes/settingPages/SettingActiveDirectoryLdap'),
);
const SharePointStatus = lazy(() => import('./routes/SharePointStatus'));
const SettingEmailCustomizations = lazy(
  () => import('./routes/settingPages/SettingEmailCustomizations'),
);
const SettingOutboundEmail = lazy(() => import('./routes/settingPages/SettingOutboundEmail'));
const MaintenancePage = lazy(() => import('./routes/MaintenancePage'));
const FilePublicLink = lazy(() => import('./routes/FilePublicLink'));
const FileShareRequests = lazy(() => import('./routes/FileShareRequests'));
const EmailsInbox = lazy(() => import('./routes/emails/EmailsInbox'));
const EmailsSent = lazy(() => import('./routes/emails/EmailsSent'));
const EmailsDrafts = lazy(() => import('./routes/emails/EmailsDrafts'));
const EmailsFavorites = lazy(() => import('./routes/emails/EmailsFavorites'));
const EmailsArchive = lazy(() => import('./routes/emails/EmailsArchive'));
const EmailsTrash = lazy(() => import('./routes/emails/EmailsTrash'));
const AccountProfile = lazy(() => import('./routes/Account/AccountProfile'));
const AccountPreferences = lazy(() => import('./routes/Account/AccountPreferences'));
const AccountSecurity = lazy(() => import('./routes/Account/AccountSecurity'));
const AccountSessions = lazy(() => import('./routes/Account/AccountSessions'));
const AccountPublicLinks = lazy(() => import('./routes/Account/AccountPublicLinks'));
const AccountLayout = lazy(() => import('./layouts/AccountLayout'));
const isMaintenanceMode = import.meta.env.VITE_APP_MAINTENANCE_MODE === 'true';
const isEmailEnabled = import.meta.env.VITE_APP_EMAIL_ENABLED === 'true';

function lazyRouteFallback() {
  return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
}

function App() {
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <Routes>
      <Route element={<ExternalLayout />}>
        <Route path="/viewer/:fileId" element={<Viewer />} />
        <Route
          path="/file-share-requests/:grantId?"
          element={
            <Suspense fallback={lazyRouteFallback()}>
              <FileShareRequests />
            </Suspense>
          }
        />
      </Route>
      <Route element={<ExternalLayout />}>
        <Route path="/document/:fileId" element={<Office type={FileTypeEnum.Word} />} />
        <Route path="/spreadsheet/:fileId" element={<Office type={FileTypeEnum.Excel} />} />
        <Route path="/presentation/:fileId" element={<Office type={FileTypeEnum.Powerpoint} />} />
        <Route path="/wopitest/:fileId" element={<Office type={FileTypeEnum.Wopitest} />} />
        {/*
         * Office editor for an encrypted SharePoint file. Its own route rather than a branch of the
         * ones above: the id is a SharePoint sharing token, there is no FileGrant file behind it, and
         * none of the FileGrant actions (share, move, download, AI) apply.
         */}
        {/*
         * The editor needs a delegated Graph token from the CUSTOMER's tenant, so it runs under its own
         * MSAL instance, built from that company's setting — see SharePointMsalProvider.
         */}
        <Route
          path="/sharepoint/editor/:fileId"
          element={
            <Suspense fallback={lazyRouteFallback()}>
              <SharePointMsalProvider>
                <SharePointOffice />
              </SharePointMsalProvider>
            </Suspense>
          }
        />
        <Route
          path="/word/:fileId"
          element={<LegacyOfficeRedirect to={LEGACY_OFFICE_REDIRECTS.word} />}
        />
        <Route
          path="/excel/:fileId"
          element={<LegacyOfficeRedirect to={LEGACY_OFFICE_REDIRECTS.excel} />}
        />
        <Route
          path="/powerpoint/:fileId"
          element={<LegacyOfficeRedirect to={LEGACY_OFFICE_REDIRECTS.powerpoint} />}
        />
      </Route>
      {/*<Route path="/payment/:fileId" element={<Payment />} />*/}
      <Route
        path="/store/:fileId"
        element={
          <Suspense fallback={lazyRouteFallback()}>
            <Store />
          </Suspense>
        }
      />
      <Route
        path="/purchase/:fileId"
        element={
          <Suspense fallback={lazyRouteFallback()}>
            <Purchase />
          </Suspense>
        }
      />
      <Route
        path="/payment/success/:fileId"
        element={
          <Suspense fallback={lazyRouteFallback()}>
            <PaymentSuccess />
          </Suspense>
        }
      />
      <Route element={<AuthLayout />}>
        <Route path="login" element={<Login />} />
        <Route path="logout" element={<Logout />} />
        <Route path="oauth/callback" element={<OAuthCallback />} />
        <Route path="mobile/auth-success" element={<MobileAuthSuccess />} />
        <Route path="extension/authorize" element={<ExtensionAuthorize />} />
        <Route path="file/:fileId" element={<Login />} />
        <Route path="file/protected/:fileUid" element={<ProtectedFile />} />
        <Route path="file/protected/:fileUid/:token" element={<ProtectedFileToken />} />
      </Route>
      {/* <Route path="files" element={<FG_FileManager />} /> */}
      <Route element={<ExternalLayout />}>
        <Route path="files" element={<Outlet />}>
          <Route
            path="public/:token"
            element={
              <Suspense
                fallback={
                  <div className="flex min-h-screen items-center justify-center">Loading...</div>
                }
              >
                <FilePublicLink />
              </Suspense>
            }
          />
          <Route
            path="public/:token/folder/:folderId"
            element={
              <Suspense
                fallback={
                  <div className="flex min-h-screen items-center justify-center">Loading...</div>
                }
              >
                <FilePublicLink />
              </Suspense>
            }
          />
        </Route>
        <Route path="quick-share" element={<QuickShare />} />
      </Route>
      <Route element={<MainLayout />}>
        <Route path="files">
          <Route path="personal" element={<PersonalFiles />}>
            <Route index />
            <Route path="preview/:fileId" />
            <Route path="folder/:folderUid" />
            <Route path="folder/:folderUid/preview/:fileId" />
          </Route>

          <Route path="received" element={<ReceivedFiles />}>
            <Route index />
            <Route path="preview/:fileId" />
            <Route path="folder/:folderUid" />
            <Route path="folder/:folderUid/preview/:fileId" />
          </Route>

          <Route path="shared" element={<SharedFiles />}>
            <Route index />
            <Route path="preview/:fileId" />
            <Route path="folder/:folderUid" />
            <Route path="folder/:folderUid/preview/:fileId" />
          </Route>

          <Route path="favorites" element={<FavoritesFiles />}>
            <Route index />
            <Route path="preview/:fileId" />
            <Route path="folder/:folderUid" />
            <Route path="folder/:folderUid/preview/:fileId" />
          </Route>

          <Route path="recent" element={<RecentFiles />}>
            <Route index />
            <Route path="preview/:fileId" />
          </Route>

          <Route path="hidden" element={<HiddenFiles />}>
            <Route index />
            <Route path="preview/:fileId" />
            <Route path="folder/:folderUid" />
            <Route path="folder/:folderUid/preview/:fileId" />
          </Route>
          <Route path="trash" element={<TrashFiles />}>
            <Route index />
            <Route path="preview/:fileId" />
          </Route>
        </Route>

        {isEmailEnabled && (
          <Route path="emails">
            <Route index element={<Navigate to="/emails/inbox" replace />} />
            <Route path="inbox/:emailId?" element={<EmailsInbox />} />
            <Route path="sent/:emailId?" element={<EmailsSent />} />
            <Route path="drafts/:emailId?" element={<EmailsDrafts />} />
            <Route path="favorites/:emailId?" element={<EmailsFavorites />} />
            <Route path="archive/:emailId?" element={<EmailsArchive />} />
            <Route path="trash/:emailId?" element={<EmailsTrash />} />
          </Route>
        )}

        <Route path="users" element={<Users />} />
        <Route path="groups" element={<Groups />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="sales-dashboard" element={<SalesDashboard />} />
        <Route path="activity-logs" element={<ActivityLogs />} />
        <Route path="antivirus-quarantine" element={<AntivirusQuarantine />} />
        <Route path="sharepoint" element={<SharePointStatus />} />
        <Route
          path="account"
          element={
            <Suspense fallback={lazyRouteFallback()}>
              <AccountLayout />
            </Suspense>
          }
        >
          <Route index element={<Navigate to="/account/profile" replace />} />
          <Route path="profile" element={<AccountProfile />} />
          <Route path="preferences" element={<AccountPreferences />} />
          <Route path="security" element={<AccountSecurity />} />
          <Route path="sessions" element={<AccountSessions />} />
          <Route path="public-links" element={<AccountPublicLinks />} />
        </Route>
        <Route
          path="settings"
          element={
            <SettingSaveProvider>
              <Outlet />
            </SettingSaveProvider>
          }
        >
          <Route index element={<Settings />} />
          <Route
            path={slugify(SettingTypeEnum.CompanyBranding)}
            element={<SettingCompanyBranding />}
          />
          <Route
            path={slugify(SettingTypeEnum.EmailCustomizations)}
            element={<SettingEmailCustomizations />}
          />
          <Route path={slugify(SettingTypeEnum.OutboundEmail)} element={<SettingOutboundEmail />} />
          <Route path={slugify(SettingTypeEnum.SingleSignOn)} element={<SettingSingleSignOn />} />
          <Route
            path={slugify(SettingTypeEnum.SessionControl)}
            element={<SettingSessionControl />}
          />
          <Route
            path={slugify(SettingTypeEnum.DeviceAccessLimitation)}
            element={<SettingDeviceAccessLimitation />}
          />
          <Route
            path={slugify(SettingTypeEnum.GroupCreationControl)}
            element={<SettingGroupCreationControl />}
          />
          <Route path={slugify(SettingTypeEnum.OnlineEditors)} element={<SettingOnlineEditors />} />
          <Route
            path={slugify(SettingTypeEnum.ContributorStorageLimit)}
            element={<SettingContributorStorageLimit />}
          />
          <Route
            path={slugify(SettingTypeEnum.CertifiedTimestamping)}
            element={<SettingCertifiedTimestamping />}
          />
          <Route
            path={slugify(SettingTypeEnum.AdminRegisteredMemberVisibility)}
            element={<SettingAdminRegisteredMemberVisibility />}
          />
          <Route
            path={slugify(SettingTypeEnum.ExternalFileSharingRestriction)}
            element={<SettingExternalFileSharingRestriction />}
          />
          <Route
            path={slugify(SettingTypeEnum.RetentionPolicies)}
            element={<SettingRetentionPolicies />}
          />
          <Route
            path={slugify(SettingTypeEnum.FolderCreationPermission)}
            element={<SettingFolderCreationPermission />}
          />
          <Route
            path={slugify(SettingTypeEnum.EditorFileManagement)}
            element={<SettingEditorFileManagement />}
          />
          <Route
            path={slugify(SettingTypeEnum.FileTypeRestriction)}
            element={<SettingFileTypeRestriction />}
          />
          <Route
            path={slugify(SettingTypeEnum.PublicAccessLink)}
            element={<SettingPublicAccessLink />}
          />
          <Route
            path={slugify(SettingTypeEnum.DownloadRestrictions)}
            element={<SettingDownloadRestrictions />}
          />
          <Route
            path={slugify(SettingTypeEnum.DownloadAllowedTags)}
            element={<SettingDownloadAllowedTags />}
          />
          <Route
            path={slugify(SettingTypeEnum.ForcedAntiCaptureMode)}
            element={<SettingForcedAntiCaptureMode />}
          />
          <Route path={slugify(SettingTypeEnum.UploadWithTag)} element={<SettingUploadWithTag />} />
          <Route
            path={slugify(SettingTypeEnum.FileTagManagement)}
            element={<SettingFileTagManagement />}
          />
          <Route
            path={slugify(SettingTypeEnum.FileTagAdditionRestriction)}
            element={<SettingFileTagAdditionRestriction />}
          />
          <Route
            path={slugify(SettingTypeEnum.FileSalesPermission)}
            element={<SettingFileSalesPermission />}
          />
          <Route
            path={slugify(SettingTypeEnum.WatermarkProtection)}
            element={<SettingWatermarkProtection />}
          />
          <Route path={slugify(SettingTypeEnum.FileSizeLimit)} element={<SettingFileSizeLimit />} />
          <Route path={slugify(SettingTypeEnum.OnBehalfMode)} element={<SettingOnBehalfMode />} />
          <Route path={slugify(SettingTypeEnum.AiTagging)} element={<SettingAiTagging />} />
          <Route
            path={slugify(SettingTypeEnum.FileDeletionRestriction)}
            element={<SettingFileDeletionRestriction />}
          />
          <Route path={slugify(SettingTypeEnum.AiFileSummary)} element={<SettingAiFileSummary />} />
          <Route path={slugify(SettingTypeEnum.AiFileChat)} element={<SettingAiFileChat />} />
          <Route path={slugify(SettingTypeEnum.AiScraping)} element={<SettingAiScraping />} />
          <Route
            path={slugify(SettingTypeEnum.ResharingPermission)}
            element={<SettingResharingPermission />}
          />
          <Route
            path={slugify(SettingTypeEnum.FileVersioningRetentionPolicies)}
            element={<SettingFileVersioningRetentionPolicies />}
          />
          <Route
            path={slugify(SettingTypeEnum.MandatoryExpirationSharedFile)}
            element={<SettingMandatoryExpirationSharedFile />}
          />
          <Route
            path={slugify(SettingTypeEnum.SecureFileLocalAccess)}
            element={<SettingSecureFileLocalAccess />}
          />
          <Route
            path={slugify(SettingTypeEnum.FileSharingApproval)}
            element={<SettingFileSharingApprovals />}
          />
          <Route
            path={slugify(SettingTypeEnum.TagBasedAccessControl)}
            element={<SettingTagBasedAccessControl />}
          />
          <Route
            path={slugify(SettingTypeEnum.MandatoryFileClassification)}
            element={<SettingMandatoryFileClassification />}
          />
          <Route
            path={slugify(SettingTypeEnum.ForcedFileInvisibility)}
            element={<SettingForcedFileInvisibility />}
          />
          <Route path={slugify(SettingTypeEnum.ShortcutLink)} element={<SettingShortcutLink />} />
          <Route
            path={slugify(SettingTypeEnum.QuickShareMode)}
            element={<SettingQuickShareMode />}
          />
          <Route path={slugify(SettingTypeEnum.TotpRequired)} element={<SettingTotpRequired />} />
          <Route
            path={slugify(SettingTypeEnum.PasskeySettings)}
            element={<SettingPasskeySettings />}
          />
          <Route path={slugify(SettingTypeEnum.AntivirusScan)} element={<SettingAntivirusScan />} />
          <Route
            path={slugify(SettingTypeEnum.AdminSettingsPermissions)}
            element={<SettingAdminSettingsPermissions />}
          />
          <Route
            path={slugify(SettingTypeEnum.IpRegionRestriction)}
            element={<SettingIpRegionRestriction />}
          />
          <Route
            path={slugify(SettingTypeEnum.SecurityAnalysis)}
            element={<SettingSecurityAnalysis />}
          />
          <Route
            path={slugify(SettingTypeEnum.ActivityLogRetention)}
            element={<SettingActivityLogRetention />}
          />
          <Route
            path={slugify(SettingTypeEnum.SharePointIntegration)}
            element={<SettingSharePointIntegration />}
          />
          <Route
            path={slugify(SettingTypeEnum.ActiveDirectoryLdap)}
            element={<SettingActiveDirectoryLdap />}
          />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
